/**
 * DeadlineBanner — top-of-page strip that surfaces the May 15
 * Texas property-tax-protest deadline plus a count of days
 * remaining.  Mounted inside the Navbar component, which wraps
 * the entire banner stack in a ResizeObserver-tracked div and
 * publishes the live height as ``--banner-stack-height`` on the
 * document root; the fixed navbar's margin-top reads that var so
 * a banner that wraps to 2 lines on mobile vs 1 line on desktop
 * pulls the navbar to exactly the right offset, and dismissing
 * the banner shrinks the var back to 0 with no leftover gap.
 *
 * Mobile copy: the long-form "until the May 15 protest deadline
 * (varies by county) · see your county →" wraps to 3+ lines on
 * narrow viewports, which made the banner taller than the
 * prerender-captured ``--banner-stack-height`` and opened a gap
 * between banner-bottom and navbar-top until JS re-measured.
 * The detail span hides at <540px so the banner stays a single
 * line ("⏳ N days left · see your county →") at every mobile
 * viewport — same height across SSR, hydration, and client
 * resize, no measurement-driven flicker.
 *
 * Why hard-coded May 15: Texas Tax Code §41.44 sets the protest
 * deadline as May 15 OR 30 days after each property's NOAV is
 * mailed (whichever is later).  We can't compute the per-property
 * extended deadline without their NOAV mailing date, so the
 * statewide statutory date is the safest universal anchor.  The
 * link goes to /property-tax-protest-deadlines-{YEAR}/ where
 * every supported county is enumerated; the desktop detail span
 * spells out "(varies by county)" so the relationship is clear.
 *
 * Year is read from REACT_APP_TAX_YEAR so the bump-next-year is a
 * one-line env change rather than a code edit.  The hide-after
 * threshold rolls automatically.
 */
import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// Resolve the protest-deadline date for the current tax year.
// Returns a Date at end-of-day local time so a banner showing on
// May 15 stays visible the whole day.
function resolveDeadline() {
  const taxYear = parseInt(
    process.env.REACT_APP_TAX_YEAR || `${new Date().getFullYear()}`,
    10,
  );
  return new Date(taxYear, 4 /* May */, 15, 23, 59, 59);
}

function daysLeftLabel(deadline, now = new Date()) {
  const msPerDay = 24 * 60 * 60 * 1000;
  const diff = deadline.getTime() - now.getTime();
  if (diff <= 0) return null; // banner self-hides past the deadline
  const days = Math.ceil(diff / msPerDay);
  if (days <= 1) return "Last day to protest · file by midnight";
  return `${days} days left to protest`;
}

const BannerStrip = styled.div`
  background: linear-gradient(180deg, #fff1ea 0%, #fbd6c4 100%);
  border-bottom: 1px solid #f5a88a;
  color: #7a3a1f;
  padding: 0.55rem 2.4rem 0.55rem 1rem;
  text-align: center;
  font-size: 0.92rem;
  font-weight: 500;
  position: relative;
  line-height: 1.35;
  letter-spacing: -0.003em;
  width: 100%;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* Detail copy hides on narrow viewports so the banner stays
     one line everywhere — keeps banner height predictable across
     SSR/hydrate/resize, which is what the navbar's margin-top
     CSS var depends on. */
  .deadline-banner-detail {
    display: none;
  }
  @media (min-width: 540px) {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    .deadline-banner-detail {
      display: inline;
    }
  }
`;

const DismissButton = styled.button`
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 0;
  color: #7a3a1f;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0 0.25rem;
  line-height: 1;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const DeadlineBanner = ({ onDismiss }) => {
  const deadline = resolveDeadline();
  const label = daysLeftLabel(deadline);
  if (!label) return null;

  const taxYear = deadline.getFullYear();

  return (
    <BannerStrip role="status" data-testid="deadline-banner">
      <span aria-hidden="true" style={{ fontSize: "1rem", marginRight: "0.3rem" }}>
        ⏳
      </span>
      <strong style={{ fontWeight: 700 }}>{label}</strong>{" "}
      <span className="deadline-banner-detail">
        before the May 15 deadline (varies by county) ·{" "}
      </span>
      <a
        href={`/property-tax-protest-deadlines-${taxYear}/`}
        style={{
          color: "#7a3a1f",
          textDecoration: "underline",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        see your county →
      </a>
      {onDismiss && (
        <DismissButton
          onClick={onDismiss}
          aria-label="Dismiss deadline banner"
        >
          ✕
        </DismissButton>
      )}
    </BannerStrip>
  );
};

DeadlineBanner.propTypes = {
  onDismiss: PropTypes.func,
};

export default DeadlineBanner;
export { resolveDeadline, daysLeftLabel };
